import { useEffect, useState } from 'react';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export const CATALOG_PATHS = {
  root: '/catalog',
  graph: '/catalog-graph',
  import: '/catalog-import',
} as const;

type CatalogTypeFacet = {
  value: string;
  count: number;
};

const capitalize = (value: string) =>
  value.length ? value[0].toLocaleUpperCase('en-US') + value.slice(1) : value;

export const buildCatalogTypeDestinations = (
  facets: readonly CatalogTypeFacet[],
) => {
  const types = new Set(
    [...facets]
      .sort((left, right) => right.count - left.count)
      .map(facet => facet.value.toLocaleLowerCase('en-US')),
  );

  return [...types].map(type => {
    const filters = new URLSearchParams({
      'filters[kind]': 'component',
      'filters[type]': type,
      'filters[user]': 'all',
    });

    return {
      title: capitalize(type),
      to: `${CATALOG_PATHS.root}?${filters}`,
    };
  });
};

export const useCatalogTypeDestinations = () => {
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const [destinations, setDestinations] = useState<
    ReturnType<typeof buildCatalogTypeDestinations>
  >([]);

  useEffect(() => {
    let active = true;

    catalogApi
      .getEntityFacets({
        filter: { kind: 'component' },
        facets: ['spec.type'],
      })
      .then(response => {
        if (active) {
          setDestinations(
            buildCatalogTypeDestinations(response.facets['spec.type'] ?? []),
          );
        }
      })
      .catch(error => {
        errorApi.post(
          error instanceof Error
            ? error
            : new Error('Failed to load catalog types for sidebar navigation'),
        );
        if (active) {
          setDestinations([]);
        }
      });

    return () => {
      active = false;
    };
  }, [catalogApi, errorApi]);

  return destinations;
};
